import { useState } from "react";
import { formatManilaDateTime } from "../../utils/manilaTime";
import { cx } from "../../utils/classNames";
import ui from "./ui.module.css";
import { approverGroupText } from "../../../utils/waitingFor";

// Multi-level approval of a request (overtime, leave). Shows
// "Pending – level x of y" while pending, and the approval history.
//   isPending:  the request is still waiting (status PENDING)
//   current / total: approval levels (total 1 = single approver, no level text)
//   approvals:  [{ level, action: "APPROVE"|"REJECT", name, at, remarks, approvedMinutes? }]
//   formatExtra(entry): optional extra text per entry (e.g. approved minutes)
//   waitingFor: everyone who can decide (any one decides; the first decision
//               is final); null/undefined = not pending
//   managersAbove: also any branch manager above the employee / HR
// Overtime, leave and corrections are one step now: no "level x of y".
const ApprovalProgress = ({
  isPending,
  current = 1,
  total = 1,
  approvals = [],
  formatExtra,
  waitingFor,
  managersAbove,
  oneStep = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const showLevel = !oneStep && isPending && total > 1;
  const showWaiting = isPending && (Array.isArray(waitingFor) || managersAbove);
  if (!showLevel && !showWaiting && approvals.length === 0) return null;

  return (
    <div className="small mt-1">
      {showLevel && (
        <div className={ui.muted}>
          Pending – level {Math.min(current, total)} of {total}
        </div>
      )}
      {showWaiting && (
        <div className={ui.muted}>
          Approvers (any one decides):{" "}
          {approverGroupText(waitingFor || [], managersAbove) ||
            "Branch manager / HR"}
        </div>
      )}

      {approvals.length > 0 && (
        <>
          <button
            type="button"
            className={ui.linkButton}
            onClick={() => setIsOpen((open) => !open)}
          >
            {isOpen ? "Hide history" : `History (${approvals.length})`}
          </button>
          {isOpen && (
            <ul className="list-unstyled mb-0 mt-1">
              {approvals.map((entry, index) => {
                const extra = formatExtra?.(entry);
                return (
                  <li key={`${entry.at}-${index}`} className="mb-1">
                    <span
                      className={cx(
                        "fw-semibold",
                        entry.action === "REJECT"
                          ? "text-danger"
                          : "text-success",
                      )}
                    >
                      {entry.change === "CANCEL"
                        ? "Cancel request "
                        : entry.change === "EDIT"
                          ? "Edit request "
                          : ""}
                      {entry.change
                        ? entry.action === "REJECT"
                          ? "rejected"
                          : "approved"
                        : entry.action === "REJECT"
                          ? "Rejected"
                          : "Approved"}
                    </span>{" "}
                    · L{entry.level} · {entry.name ?? "Approver"}
                    {extra && ` · ${extra}`}
                    <div className={ui.muted}>
                      {formatManilaDateTime(entry.at)}
                    </div>
                    {entry.remarks && (
                      <div className={ui.muted}>“{entry.remarks}”</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default ApprovalProgress;
